import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { superAdminApi } from '../../api/superAdminApi';
import {
  SuperAdminDashboardData,
  TenantDto,
  CreateTenant,
  UpdateTenant,
  SubscriptionPlanDto,
  CreateSubscriptionPlan,
  UpdateSubscriptionPlan,
  InvoiceDto,
  GenerateInvoice,
  UpdateInvoice,
  SupportTicketDto,
  CreateSupportTicket,
  UpdateSupportTicket,
  UpdateTicketStatus,
  FeatureFlagDto,
  CreateFeatureFlag,
  UpdateFeatureFlag,
  RoleTemplateDto,
  CreateRoleTemplate,
  UpdateRoleTemplate,
  PlatformAnalyticsDto,
  NotificationDto,
  CreateNotification,
  AuditLogDto,
  UsageMonitoringDto,
  SystemSettingsDto,
  UpdateSystemSetting,
} from '../../types/superAdminTypes';

interface SuperAdminState {
  dashboard: SuperAdminDashboardData | null;
  tenants: TenantDto[];
  selectedTenant: TenantDto | null;
  subscriptionPlans: SubscriptionPlanDto[];
  invoices: InvoiceDto[];
  supportTickets: SupportTicketDto[];
  featureFlags: FeatureFlagDto[];
  roleTemplates: RoleTemplateDto[];
  analytics: PlatformAnalyticsDto | null;
  notifications: NotificationDto[];
  auditLogs: AuditLogDto[];
  usageMonitoring: UsageMonitoringDto[];
  systemSettings: SystemSettingsDto[];
  loading: boolean;
  error: string | null;
}

const getApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;

  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  if (data?.message && typeof data.message === 'string') {
    return data.message;
  }

  if (data?.title && typeof data.title === 'string') {
    return data.title;
  }

  if (data?.errors && typeof data.errors === 'object') {
    const firstKey = Object.keys(data.errors)[0];
    const firstError = firstKey ? data.errors[firstKey]?.[0] : null;
    if (typeof firstError === 'string' && firstError.trim()) {
      return firstError;
    }
  }

  return fallback;
};

const initialState: SuperAdminState = {
  dashboard: null,
  tenants: [],
  selectedTenant: null,
  subscriptionPlans: [],
  invoices: [],
  supportTickets: [],
  featureFlags: [],
  roleTemplates: [],
  analytics: null,
  notifications: [],
  auditLogs: [],
  usageMonitoring: [],
  systemSettings: [],
  loading: false,
  error: null,
};

// ─── Dashboard Thunks ───────────────────────────────────────

export const fetchSuperAdminDashboard = createAsyncThunk(
  'superAdmin/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      return await superAdminApi.getDashboard();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboard');
    }
  }
);

// ─── Tenant Thunks ──────────────────────────────────────────

export const fetchTenants = createAsyncThunk(
  'superAdmin/fetchTenants',
  async (params: { searchTerm?: string; status?: number; planId?: string } = {}, { rejectWithValue }) => {
    try {
      return await superAdminApi.getTenants(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tenants');
    }
  }
);

export const fetchTenantById = createAsyncThunk(
  'superAdmin/fetchTenantById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await superAdminApi.getTenantById(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tenant');
    }
  }
);

export const createTenant = createAsyncThunk(
  'superAdmin/createTenant',
  async (data: CreateTenant, { rejectWithValue }) => {
    try {
      return await superAdminApi.createTenant(data);
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create tenant'));
    }
  }
);

export const updateTenant = createAsyncThunk(
  'superAdmin/updateTenant',
  async ({ id, data }: { id: string; data: UpdateTenant }, { rejectWithValue }) => {
    try {
      await superAdminApi.updateTenant(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update tenant'));
    }
  }
);

export const activateTenant = createAsyncThunk(
  'superAdmin/activateTenant',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.activateTenant(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to activate tenant');
    }
  }
);

export const suspendTenant = createAsyncThunk(
  'superAdmin/suspendTenant',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.suspendTenant(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to suspend tenant');
    }
  }
);

export const deleteTenant = createAsyncThunk(
  'superAdmin/deleteTenant',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.deleteTenant(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete tenant');
    }
  }
);

// ─── Subscription Plan Thunks ───────────────────────────────

export const fetchSubscriptionPlans = createAsyncThunk(
  'superAdmin/fetchSubscriptionPlans',
  async (_, { rejectWithValue }) => {
    try {
      return await superAdminApi.getSubscriptionPlans();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscription plans');
    }
  }
);

export const createSubscriptionPlan = createAsyncThunk(
  'superAdmin/createSubscriptionPlan',
  async (data: CreateSubscriptionPlan, { rejectWithValue }) => {
    try {
      return await superAdminApi.createSubscriptionPlan(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create subscription plan');
    }
  }
);

export const updateSubscriptionPlan = createAsyncThunk(
  'superAdmin/updateSubscriptionPlan',
  async ({ id, data }: { id: string; data: UpdateSubscriptionPlan }, { rejectWithValue }) => {
    try {
      await superAdminApi.updateSubscriptionPlan(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update subscription plan');
    }
  }
);

export const deleteSubscriptionPlan = createAsyncThunk(
  'superAdmin/deleteSubscriptionPlan',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.deleteSubscriptionPlan(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete subscription plan');
    }
  }
);

export const activateSubscriptionPlan = createAsyncThunk(
  'superAdmin/activateSubscriptionPlan',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.activateSubscriptionPlan(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to activate subscription plan');
    }
  }
);

export const deactivateSubscriptionPlan = createAsyncThunk(
  'superAdmin/deactivateSubscriptionPlan',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.deactivateSubscriptionPlan(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to deactivate subscription plan');
    }
  }
);

// ─── Invoice Thunks ─────────────────────────────────────────

export const fetchInvoices = createAsyncThunk(
  'superAdmin/fetchInvoices',
  async (params: { tenantId?: string; paymentStatus?: number; fromDate?: string; toDate?: string } = {}, { rejectWithValue }) => {
    try {
      return await superAdminApi.getInvoices(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoices');
    }
  }
);

export const generateInvoice = createAsyncThunk(
  'superAdmin/generateInvoice',
  async (data: GenerateInvoice, { rejectWithValue }) => {
    try {
      return await superAdminApi.generateInvoice(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate invoice');
    }
  }
);

export const updateInvoice = createAsyncThunk(
  'superAdmin/updateInvoice',
  async ({ id, data }: { id: string; data: Partial<UpdateInvoice> }, { rejectWithValue }) => {
    try {
      await superAdminApi.updateInvoice(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update invoice');
    }
  }
);

export const markInvoicePaid = createAsyncThunk(
  'superAdmin/markInvoicePaid',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.markInvoicePaid(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark invoice as paid');
    }
  }
);

// ─── Support Ticket Thunks ──────────────────────────────────

export const fetchSupportTickets = createAsyncThunk(
  'superAdmin/fetchSupportTickets',
  async (params: { tenantId?: string; priority?: number; status?: number; searchTerm?: string } = {}, { rejectWithValue }) => {
    try {
      return await superAdminApi.getSupportTickets(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch support tickets');
    }
  }
);

export const createSupportTicket = createAsyncThunk(
  'superAdmin/createSupportTicket',
  async (data: CreateSupportTicket, { rejectWithValue }) => {
    try {
      return await superAdminApi.createSupportTicket(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create support ticket');
    }
  }
);

export const updateTicketStatus = createAsyncThunk(
  'superAdmin/updateTicketStatus',
  async ({ id, data }: { id: string; data: UpdateTicketStatus }, { rejectWithValue }) => {
    try {
      await superAdminApi.updateTicketStatus(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update ticket status');
    }
  }
);

export const updateSupportTicket = createAsyncThunk(
  'superAdmin/updateSupportTicket',
  async ({ id, data }: { id: string; data: UpdateSupportTicket }, { rejectWithValue }) => {
    try {
      await superAdminApi.updateSupportTicket(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update support ticket');
    }
  }
);

// ─── Feature Flag Thunks ────────────────────────────────────

export const fetchFeatureFlags = createAsyncThunk(
  'superAdmin/fetchFeatureFlags',
  async (_, { rejectWithValue }) => {
    try {
      return await superAdminApi.getFeatureFlags();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch feature flags');
    }
  }
);

export const createFeatureFlag = createAsyncThunk(
  'superAdmin/createFeatureFlag',
  async (data: CreateFeatureFlag, { rejectWithValue }) => {
    try {
      return await superAdminApi.createFeatureFlag(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create feature flag');
    }
  }
);

export const updateFeatureFlag = createAsyncThunk(
  'superAdmin/updateFeatureFlag',
  async ({ id, data }: { id: string; data: UpdateFeatureFlag }, { rejectWithValue }) => {
    try {
      await superAdminApi.updateFeatureFlag(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update feature flag');
    }
  }
);

export const deleteFeatureFlag = createAsyncThunk(
  'superAdmin/deleteFeatureFlag',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.deleteFeatureFlag(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete feature flag');
    }
  }
);

// ─── Role Template Thunks ───────────────────────────────────

export const fetchRoleTemplates = createAsyncThunk(
  'superAdmin/fetchRoleTemplates',
  async (_, { rejectWithValue }) => {
    try {
      return await superAdminApi.getRoleTemplates();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch role templates');
    }
  }
);

export const createRoleTemplate = createAsyncThunk(
  'superAdmin/createRoleTemplate',
  async (data: CreateRoleTemplate, { rejectWithValue }) => {
    try {
      return await superAdminApi.createRoleTemplate(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create role template');
    }
  }
);

export const updateRoleTemplate = createAsyncThunk(
  'superAdmin/updateRoleTemplate',
  async ({ id, data }: { id: string; data: UpdateRoleTemplate }, { rejectWithValue }) => {
    try {
      await superAdminApi.updateRoleTemplate(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update role template');
    }
  }
);

export const deleteRoleTemplate = createAsyncThunk(
  'superAdmin/deleteRoleTemplate',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.deleteRoleTemplate(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete role template');
    }
  }
);

// ─── Analytics Thunks ───────────────────────────────────────

export const fetchPlatformAnalytics = createAsyncThunk(
  'superAdmin/fetchAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      return await superAdminApi.getPlatformAnalytics();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics');
    }
  }
);

// ─── Notification Thunks ────────────────────────────────────

export const fetchNotifications = createAsyncThunk(
  'superAdmin/fetchNotifications',
  async (params: { isSent?: boolean; channel?: number } = {}, { rejectWithValue }) => {
    try {
      return await superAdminApi.getNotifications(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notifications');
    }
  }
);

export const createNotification = createAsyncThunk(
  'superAdmin/createNotification',
  async (data: CreateNotification, { rejectWithValue }) => {
    try {
      return await superAdminApi.createNotification(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create notification');
    }
  }
);

export const updateNotification = createAsyncThunk(
  'superAdmin/updateNotification',
  async (
    payload: { id: string; data: CreateNotification },
    { rejectWithValue }
  ) => {
    try {
      await superAdminApi.updateNotification(payload.id, payload.data);
      return payload.id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update notification');
    }
  }
);

export const sendNotification = createAsyncThunk(
  'superAdmin/sendNotification',
  async (id: string, { rejectWithValue }) => {
    try {
      await superAdminApi.sendNotification(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send notification');
    }
  }
);

// ─── Audit Log Thunks ───────────────────────────────────────

export const fetchAuditLogs = createAsyncThunk(
  'superAdmin/fetchAuditLogs',
  async (
    params: { userId?: string; tenantId?: string; action?: string; fromDate?: string; toDate?: string; searchTerm?: string } = {},
    { rejectWithValue }
  ) => {
    try {
      return await superAdminApi.getAuditLogs(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch audit logs');
    }
  }
);

// ─── Usage Monitoring Thunks ────────────────────────────────

export const fetchUsageMonitoring = createAsyncThunk(
  'superAdmin/fetchUsageMonitoring',
  async (params: { searchTerm?: string } = {}, { rejectWithValue }) => {
    try {
      return await superAdminApi.getUsageMonitoring(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch usage monitoring');
    }
  }
);

// ─── System Settings Thunks ─────────────────────────────────

export const fetchSystemSettings = createAsyncThunk(
  'superAdmin/fetchSystemSettings',
  async (params: { category?: string } = {}, { rejectWithValue }) => {
    try {
      return await superAdminApi.getSystemSettings(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch system settings');
    }
  }
);

export const updateSystemSetting = createAsyncThunk(
  'superAdmin/updateSystemSetting',
  async ({ id, data }: { id: string; data: UpdateSystemSetting }, { rejectWithValue }) => {
    try {
      await superAdminApi.updateSystemSetting(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update system setting');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────

const superAdminSlice = createSlice({
  name: 'superAdmin',
  initialState,
  reducers: {
    clearSuperAdminError: (state) => {
      state.error = null;
    },
    clearSelectedTenant: (state) => {
      state.selectedTenant = null;
    },
  },
  extraReducers: (builder) => {
    // Dashboard
    builder
      .addCase(fetchSuperAdminDashboard.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSuperAdminDashboard.fulfilled, (state, action: PayloadAction<SuperAdminDashboardData>) => { state.loading = false; state.dashboard = action.payload; })
      .addCase(fetchSuperAdminDashboard.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

    // Tenants
    builder
      .addCase(fetchTenants.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTenants.fulfilled, (state, action: PayloadAction<TenantDto[]>) => { state.loading = false; state.tenants = action.payload; })
      .addCase(fetchTenants.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
    builder
      .addCase(fetchTenantById.fulfilled, (state, action: PayloadAction<TenantDto>) => { state.selectedTenant = action.payload; });
    builder
      .addCase(deleteTenant.fulfilled, (state, action: PayloadAction<string>) => { state.tenants = state.tenants.filter(t => t.id !== action.payload); });

    // Subscription Plans
    builder
      .addCase(fetchSubscriptionPlans.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action: PayloadAction<SubscriptionPlanDto[]>) => { state.loading = false; state.subscriptionPlans = action.payload; })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
    builder
      .addCase(deleteSubscriptionPlan.fulfilled, (state, action: PayloadAction<string>) => {
        const plan = state.subscriptionPlans.find(p => p.id === action.payload);
        if (plan) {
          plan.isActive = false;
        }
      });
    builder
      .addCase(activateSubscriptionPlan.fulfilled, (state, action: PayloadAction<string>) => {
        const id = action.payload;
        const plan = state.subscriptionPlans.find(p => p.id === id);
        if (plan) {
          plan.isActive = true;
        }
      });
    builder
      .addCase(deactivateSubscriptionPlan.fulfilled, (state, action: PayloadAction<string>) => {
        const plan = state.subscriptionPlans.find(p => p.id === action.payload);
        if (plan) {
          plan.isActive = false;
        }
      });

    // Invoices
    builder
      .addCase(fetchInvoices.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchInvoices.fulfilled, (state, action: PayloadAction<InvoiceDto[]>) => { state.loading = false; state.invoices = action.payload; })
      .addCase(fetchInvoices.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

    // Support Tickets
    builder
      .addCase(fetchSupportTickets.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSupportTickets.fulfilled, (state, action: PayloadAction<SupportTicketDto[]>) => { state.loading = false; state.supportTickets = action.payload; })
      .addCase(fetchSupportTickets.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

    // Feature Flags
    builder
      .addCase(fetchFeatureFlags.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFeatureFlags.fulfilled, (state, action: PayloadAction<FeatureFlagDto[]>) => { state.loading = false; state.featureFlags = action.payload; })
      .addCase(fetchFeatureFlags.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
    builder
      .addCase(deleteFeatureFlag.fulfilled, (state, action: PayloadAction<string>) => { state.featureFlags = state.featureFlags.filter(f => f.id !== action.payload); });

    // Role Templates
    builder
      .addCase(fetchRoleTemplates.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchRoleTemplates.fulfilled, (state, action: PayloadAction<RoleTemplateDto[]>) => { state.loading = false; state.roleTemplates = action.payload; })
      .addCase(fetchRoleTemplates.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
    builder
      .addCase(deleteRoleTemplate.fulfilled, (state, action: PayloadAction<string>) => { state.roleTemplates = state.roleTemplates.filter(r => r.id !== action.payload); });

    // Analytics
    builder
      .addCase(fetchPlatformAnalytics.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPlatformAnalytics.fulfilled, (state, action: PayloadAction<PlatformAnalyticsDto>) => { state.loading = false; state.analytics = action.payload; })
      .addCase(fetchPlatformAnalytics.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

    // Notifications
    builder
      .addCase(fetchNotifications.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchNotifications.fulfilled, (state, action: PayloadAction<NotificationDto[]>) => { state.loading = false; state.notifications = action.payload; })
      .addCase(fetchNotifications.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

    // Audit Logs
    builder
      .addCase(fetchAuditLogs.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAuditLogs.fulfilled, (state, action: PayloadAction<AuditLogDto[]>) => { state.loading = false; state.auditLogs = action.payload; })
      .addCase(fetchAuditLogs.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

    // Usage Monitoring
    builder
      .addCase(fetchUsageMonitoring.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUsageMonitoring.fulfilled, (state, action: PayloadAction<UsageMonitoringDto[]>) => { state.loading = false; state.usageMonitoring = action.payload; })
      .addCase(fetchUsageMonitoring.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

    // System Settings
    builder
      .addCase(fetchSystemSettings.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSystemSettings.fulfilled, (state, action: PayloadAction<SystemSettingsDto[]>) => { state.loading = false; state.systemSettings = action.payload; })
      .addCase(fetchSystemSettings.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
  },
});

export const { clearSuperAdminError, clearSelectedTenant } = superAdminSlice.actions;
export default superAdminSlice.reducer;
